/**
 * Raster — integer shape primitives that draw *materials* into a cell grid.
 *
 * Everything the creature is made of is one of four shapes: a rotated ellipse, a
 * triangle, a line, and a single point. This file is those four and nothing else, so
 * the anatomy above it is pure geometry and the Solidity mirror is four loops.
 *
 * **Coordinates are Q8 sub-cell** (`SUB` = 256), origin at the grid's top-left, `+y`
 * downward, `+x` the direction the creature faces. A cell's *centre* sits at
 * `(x * SUB + SUB/2, y * SUB + SUB/2)` and that centre is the only thing tested
 * against a shape. Centre sampling is what makes a sub-cell feature honest: an eye of
 * 0.3 cells covers no centre and so draws nothing, which is correct — at 32x32 it
 * cannot be drawn — and it is the caller's job to notice, which is why every fill
 * returns how many cells it actually wrote.
 *
 * **A material grid, not a cell grid.** These functions write material ids, not packed
 * cell bytes. Materials are what layering, masking and the outline pass all reason
 * about; `compose.ts` resolves them to bytes once, at the end, where the per-cell
 * texture decisions happen. Drawing straight to bytes would mean the outline pass had
 * to reverse-engineer "is this the creature?" out of a glyph index.
 *
 * **Overflow.** The largest intermediate is an ellipse test: `u * u * ry * ry` with
 * `u` under 2^13 and `ry` under 2^11, so under 2^48 — inside JavaScript's exact
 * integer range, and every term is a square and therefore non-negative, which lets
 * `Ellipse.sol` stay in `uint256` with no sign handling.
 */

import { CELL_COUNT, GRID_COLS, GRID_ROWS, SUB, TRIG_ONE, TRIG_STEPS } from "../constants.js";
import { MAT_EMPTY } from "./materials.js";
import { cellIndex, inBounds } from "./cell.js";
import { clamp, shr } from "../engine/int.js";
import { cos, sin } from "../engine/trig.js";

/**
 * One material id per cell, row-major, `MAT_EMPTY` where nothing has been drawn.
 * Terrain produces one of these too, so the two are the same kind of thing.
 */
export type MaterialGrid = Uint8Array;

export function newMaterialGrid(): MaterialGrid {
  return new Uint8Array(CELL_COUNT);
}

/** Mask value meaning "write regardless of what is already here". */
export const OVER_ANY = 0xff;

/** The cell containing a Q8 coordinate. Positions floor; lengths round (see `cells`). */
export function cellOf(q8: number): number {
  return shr(q8, 8);
}

/** The Q8 centre of cell `c` along one axis. */
export function cellCentre(c: number): number {
  return c * SUB + SUB / 2;
}

export function materialAt(g: MaterialGrid, x: number, y: number): number {
  return inBounds(x, y) ? (g[cellIndex(x, y)] as number) : MAT_EMPTY;
}

/** Write a material. Off-grid writes are dropped, matching `put()` in `cell.ts`. */
export function paint(g: MaterialGrid, x: number, y: number, material: number): number {
  if (!inBounds(x, y)) return 0;
  g[cellIndex(x, y)] = material;
  return 1;
}

/** Write only where the cell currently holds `over`, or anywhere for `OVER_ANY`. */
export function paintOver(
  g: MaterialGrid,
  x: number,
  y: number,
  material: number,
  over: number,
): number {
  if (!inBounds(x, y)) return 0;
  const idx = cellIndex(x, y);
  if (over !== OVER_ANY && g[idx] !== over) return 0;
  g[idx] = material;
  return 1;
}

/** The cell containing a Q8 point. Used for features smaller than one cell. */
export function plot(g: MaterialGrid, sx: number, sy: number, material: number): number {
  return paint(g, cellOf(sx), cellOf(sy), material);
}

/**
 * Filled ellipse, optionally rotated, optionally masked.
 *
 * `angle` is in trig steps and rotates the ellipse's major axis from `+x` toward `+y`,
 * which on screen is clockwise — so a positive `lean` tips the creature's front down,
 * exactly as `archetypes.ts` documents it.
 *
 * The rotation is applied to the *offset being tested*, not to the shape, so there is
 * no need to transform the ellipse's coefficients: rotate the sample into the
 * ellipse's own frame and use the axis-aligned test. Two multiplies per cell.
 *
 * Returns the number of cells written.
 */
export function fillEllipse(
  g: MaterialGrid,
  material: number,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  angle = 0,
  over: number = OVER_ANY,
): number {
  if (rx <= 0 || ry <= 0) return 0;
  // Rotation grows the axis-aligned bounds; the larger radius bounds both axes.
  const reach = rx > ry ? rx : ry;
  const x0 = clamp(cellOf(cx - reach), 0, GRID_COLS - 1);
  const x1 = clamp(cellOf(cx + reach), 0, GRID_COLS - 1);
  const y0 = clamp(cellOf(cy - reach), 0, GRID_ROWS - 1);
  const y1 = clamp(cellOf(cy + reach), 0, GRID_ROWS - 1);
  const limit = rx * rx * ry * ry;
  const s = angle === 0 ? 0 : sin(angle);
  const c = angle === 0 ? TRIG_ONE : cos(angle);

  let drawn = 0;
  for (let y = y0; y <= y1; y++) {
    const dy = cellCentre(y) - cy;
    for (let x = x0; x <= x1; x++) {
      const dx = cellCentre(x) - cx;
      const u = angle === 0 ? dx : shr(dx * c + dy * s, 12);
      const v = angle === 0 ? dy : shr(dy * c - dx * s, 12);
      if (u * u * ry * ry + v * v * rx * rx <= limit) {
        drawn += paintOver(g, x, y, material, over);
      }
    }
  }
  return drawn;
}

/** Circle. Same test with equal radii, kept separate because it reads better. */
export function fillDisc(
  g: MaterialGrid,
  material: number,
  cx: number,
  cy: number,
  r: number,
  over: number = OVER_ANY,
): number {
  return fillEllipse(g, material, cx, cy, r, r, 0, over);
}

/**
 * Filled triangle by half-plane test, from Q8 vertices in either winding.
 *
 * A cell is inside when its centre is on the same side of all three edges. The three
 * cross products are compared by sign only, so nothing is normalised and no division
 * happens. Degenerate triangles — the three points collinear, which a zero-length beak
 * produces — write nothing, and the caller is expected to have a line as a fallback.
 *
 * That last promise needs the explicit area test below, because the half-plane test on
 * its own does not keep it: collinear vertices make all three cross products zero for
 * any centre *on* the line, so a segment that happens to lie exactly along a row of cell
 * centres would fill that whole row while the same segment half a cell up filled
 * nothing. One multiply, once, buys a rule with no knife edge in it.
 *
 * Takes the same `over` mask as `fillEllipse`, for the same reason: the folded wing is a
 * triangle that must stay inside the body it lies on. `strokeLine` deliberately has no
 * mask — every line in the anatomy is a feather, a leg or a crest, and all of those are
 * meant to leave the silhouette.
 */
export function fillTriangle(
  g: MaterialGrid,
  material: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
  over: number = OVER_ANY,
): number {
  if ((bx - ax) * (cy - ay) - (by - ay) * (cx - ax) === 0) return 0;

  const minX = clamp(cellOf(Math.min(ax, bx, cx)), 0, GRID_COLS - 1);
  const maxX = clamp(cellOf(Math.max(ax, bx, cx)), 0, GRID_COLS - 1);
  const minY = clamp(cellOf(Math.min(ay, by, cy)), 0, GRID_ROWS - 1);
  const maxY = clamp(cellOf(Math.max(ay, by, cy)), 0, GRID_ROWS - 1);

  let drawn = 0;
  for (let y = minY; y <= maxY; y++) {
    const py = cellCentre(y);
    for (let x = minX; x <= maxX; x++) {
      const px = cellCentre(x);
      const e0 = (bx - ax) * (py - ay) - (by - ay) * (px - ax);
      const e1 = (cx - bx) * (py - by) - (cy - by) * (px - bx);
      const e2 = (ax - cx) * (py - cy) - (ay - cy) * (px - cx);
      const inside = (e0 >= 0 && e1 >= 0 && e2 >= 0) || (e0 <= 0 && e1 <= 0 && e2 <= 0);
      if (inside) drawn += paintOver(g, x, y, material, over);
    }
  }
  return drawn;
}

/**
 * Bresenham line between two Q8 points, one cell wide.
 *
 * The endpoints are floored to their containing cells first, so sub-cell precision
 * survives as *which* cell a limb starts and ends in rather than as anti-aliasing —
 * there is no anti-aliasing to be had in a glyph grid. Always draws at least the start
 * cell, which is what makes a zero-length limb still mark its joint.
 */
export function strokeLine(
  g: MaterialGrid,
  material: number,
  sx0: number,
  sy0: number,
  sx1: number,
  sy1: number,
): number {
  let x = cellOf(sx0);
  let y = cellOf(sy0);
  const x1 = cellOf(sx1);
  const y1 = cellOf(sy1);
  const dx = x1 > x ? x1 - x : x - x1;
  const dy = y1 > y ? y1 - y : y - y1;
  const stepX = x1 >= x ? 1 : -1;
  const stepY = y1 >= y ? 1 : -1;
  let err = dx - dy;

  let drawn = 0;
  // Bounded by the grid diagonal plus one, so a bad argument cannot spin forever.
  for (let guard = 0; guard <= GRID_COLS + GRID_ROWS; guard++) {
    drawn += paint(g, x, y, material);
    if (x === x1 && y === y1) break;
    const e2 = 2 * err;
    if (e2 > -dy) {
      err -= dy;
      x += stepX;
    }
    if (e2 < dx) {
      err += dx;
      y += stepY;
    }
  }
  return drawn;
}

/**
 * Dilate the drawn shape by one cell in the four cardinal directions, writing
 * `material` into the empty cells around it.
 *
 * Four-adjacency, not eight: a diagonal outline closes the gap a diagonal silhouette
 * edge leaves, which thickens every slope by a cell and makes small birds look
 * inflated. The pass reads a snapshot, so the outline cannot outline itself and the
 * result does not depend on scan order.
 *
 * `seeds` decides which materials the dilation grows *from*. It defaults to all of
 * them, which is the pure geometric pass; `anatomy.ts` passes `seedsOutline`, because
 * a rim around a part that already shares the rim's colour is not an edge but a wider
 * part. The predicate is applied to the snapshot, so excusing a material also stops it
 * seeding through a neighbour.
 */
export function outlinePass(
  g: MaterialGrid,
  material: number,
  seeds: (m: number) => boolean = () => true,
): number {
  const src = Uint8Array.from(g);
  let drawn = 0;
  const grows = (i: number): boolean => {
    const m = src[i] as number;
    return m !== MAT_EMPTY && seeds(m);
  };
  for (let y = 0; y < GRID_ROWS; y++) {
    for (let x = 0; x < GRID_COLS; x++) {
      if (src[cellIndex(x, y)] !== MAT_EMPTY) continue;
      const touching =
        (x > 0 && grows(cellIndex(x - 1, y))) ||
        (x < GRID_COLS - 1 && grows(cellIndex(x + 1, y))) ||
        (y > 0 && grows(cellIndex(x, y - 1))) ||
        (y < GRID_ROWS - 1 && grows(cellIndex(x, y + 1)));
      if (touching) drawn += paint(g, x, y, material);
    }
  }
  return drawn;
}

/** How many cells hold a given material. Used by gates and tools, not by the render. */
export function countMaterial(g: MaterialGrid, material: number): number {
  let n = 0;
  for (let i = 0; i < g.length; i++) if (g[i] === material) n++;
  return n;
}

/** How many cells hold anything at all. */
export function countDrawn(g: MaterialGrid): number {
  let n = 0;
  for (let i = 0; i < g.length; i++) if (g[i] !== MAT_EMPTY) n++;
  return n;
}

/**
 * Cell bounding box of everything drawn, or `null` for an empty grid. This is the
 * measurement that proves a creature is inside its frame rather than merely budgeted
 * to be.
 */
export function drawnBounds(
  g: MaterialGrid,
): { x0: number; y0: number; x1: number; y1: number } | null {
  let x0 = GRID_COLS;
  let y0 = GRID_ROWS;
  let x1 = -1;
  let y1 = -1;
  for (let y = 0; y < GRID_ROWS; y++) {
    for (let x = 0; x < GRID_COLS; x++) {
      if (g[cellIndex(x, y)] === MAT_EMPTY) continue;
      if (x < x0) x0 = x;
      if (x > x1) x1 = x;
      if (y < y0) y0 = y;
      if (y > y1) y1 = y;
    }
  }
  return x1 < 0 ? null : { x0, y0, x1, y1 };
}

/** Structural guard invoked by tests. */
export function validateRaster(): void {
  const probe = 1;

  for (let c = 0; c < GRID_COLS; c++) {
    if (cellOf(cellCentre(c)) !== c) throw new Error("a cell centre is not inside its own cell");
  }

  // Centre sampling: half a cell across, on a centre, is exactly one cell.
  const one = newMaterialGrid();
  if (fillDisc(one, probe, cellCentre(4), cellCentre(4), SUB / 2, OVER_ANY) !== 1) {
    throw new Error("a half-cell disc on a cell centre must cover exactly one cell");
  }
  // And a feature far below a cell draws nothing at all, which the caller must notice.
  const tiny = newMaterialGrid();
  if (fillDisc(tiny, probe, cellCentre(4) + SUB / 2, cellCentre(4) + SUB / 2, SUB / 8) !== 0) {
    throw new Error("a sub-cell disc between centres must draw nothing");
  }

  // A quarter turn must exchange the two radii exactly.
  const wide = newMaterialGrid();
  const tall = newMaterialGrid();
  const cxy = cellCentre(15);
  fillEllipse(wide, probe, cxy, cxy, 4 * SUB, SUB, 0);
  fillEllipse(tall, probe, cxy, cxy, SUB, 4 * SUB, TRIG_STEPS / 4);
  for (let i = 0; i < CELL_COUNT; i++) {
    if (wide[i] !== tall[i]) throw new Error("rotating an ellipse a quarter turn did not transpose it");
  }

  // A right triangle covers about half its box; "about" because centres are sampled.
  const tri = newMaterialGrid();
  const area = fillTriangle(tri, probe, cellCentre(4), cellCentre(4), cellCentre(13), cellCentre(4), cellCentre(4), cellCentre(13));
  if (area < 40 || area > 60) throw new Error(`a 10x10 right triangle covered ${area} cells`);

  // The knife-edge case: collinear along a row of cell centres, where every cross
  // product is zero and the half-plane test alone would fill the whole row.
  const flat = newMaterialGrid();
  const on = cellCentre(6);
  if (fillTriangle(flat, probe, cellCentre(4), on, cellCentre(10), on, cellCentre(16), on) !== 0) {
    throw new Error("a collinear triangle must write nothing, whatever it is aligned with");
  }

  const line = newMaterialGrid();
  if (strokeLine(line, probe, cellCentre(2), cellCentre(2), cellCentre(2), cellCentre(2)) !== 1) {
    throw new Error("a zero-length line must still mark its own cell");
  }
  if (strokeLine(line, probe, cellCentre(0), cellCentre(0), cellCentre(31), cellCentre(31)) !== 32) {
    throw new Error("a full diagonal must be one cell per column");
  }

  const dot = newMaterialGrid();
  paint(dot, 10, 10, probe);
  if (outlinePass(dot, probe + 1) !== 4) throw new Error("a single cell must outline to four neighbours");
  const edge = newMaterialGrid();
  paint(edge, 0, 0, probe);
  if (outlinePass(edge, probe + 1) !== 2) throw new Error("an outline must be clipped by the frame");

  // A skipped material must not grow a rim, and must not stop its neighbour growing one.
  // Two dots two cells apart share the cell between them, so a pass that seeds from only
  // one of them still fills that cell — from the side that is allowed to.
  const pair = newMaterialGrid();
  paint(pair, 10, 10, probe);
  paint(pair, 12, 10, probe + 1);
  const grown = outlinePass(pair, probe + 2, (m) => m === probe);
  if (grown !== 4) throw new Error(`a seeded dot must rim four cells and a skipped one none, got ${grown}`);
  if (materialAt(pair, 13, 10) !== MAT_EMPTY) throw new Error("outlinePass grew from a material it was told to skip");

  if (drawnBounds(newMaterialGrid()) !== null) throw new Error("an empty grid has no bounds");
}






